import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Row, Col, Table, Form } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [client, setClient] = useState(null);
  const [clientProjects, setClientProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });
  
  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        // Fetch client details
        const clientResponse = await fetch(`/api/customers/clients/${id}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!clientResponse.ok) {
          throw new Error('Failed to fetch client details');
        }
        
        const clientData = await clientResponse.json();
        setClient(clientData);
        
        // Initialize edit form
        setEditFormData({
          name: clientData.Name || '',
          email: clientData.Email || '',
          phone: clientData.Phone || '',
          address: clientData.Address || '',
          city: clientData.City || '',
          state: clientData.State || '',
          zipCode: clientData.ZipCode || ''
        });
        
        // Fetch projects for this client
        const projectsResponse = await fetch('/api/projects?noPagination=true', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!projectsResponse.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const projectsData = await projectsResponse.json();
        
        // Ensure projectsData is an array before filtering
        const projectsArray = Array.isArray(projectsData) ? projectsData : (projectsData.data || []);
        
        // Filter projects for this client
        const filteredProjects = projectsArray.filter(
          project => project.ClientId === parseInt(id)
        );
        
        setClientProjects(filteredProjects);
      } catch (err) {
        console.error('Error fetching client data:', err);
        toast.error('Failed to load client data');
        navigate('/clients');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchClientData();
  }, [id, token, navigate]);
  
  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };
  
  // Handle update client
  const handleUpdateClient = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`/api/customers/clients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          name: editFormData.name,
          email: editFormData.email,
          phone: editFormData.phone,
          address: editFormData.address,
          city: editFormData.city,
          state: editFormData.state,
          zipCode: editFormData.zipCode
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update client');
      }
      
      const updatedClient = await response.json();
      
      // Update client state
      setClient(updatedClient);
      setIsEditing(false);
      toast.success('Client updated successfully');
    } catch (err) {
      console.error('Error updating client:', err);
      toast.error('Failed to update client');
    }
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading client details...</div>;
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <h2 className="mb-0 me-3">{client.Name}</h2>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            Edit Client
          </Button>
        </div>
        <Button 
          as={Link} 
          to="/clients"
          variant="secondary"
        >
          Back to Clients
        </Button>
      </div>
      
      <Row className="mb-4">
        <Col lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Client Information</h5>
            </Card.Header>
            <Card.Body>
              {isEditing ? (
                // Edit Form
                <Form onSubmit={handleUpdateClient}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={editFormData.name}
                      onChange={handleEditFormChange}
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={editFormData.email}
                      onChange={handleEditFormChange}
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone"
                      value={editFormData.phone}
                      onChange={handleEditFormChange}
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={editFormData.address}
                      onChange={handleEditFormChange}
                    />
                  </Form.Group>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={editFormData.city}
                          onChange={handleEditFormChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          name="state"
                          value={editFormData.state}
                          onChange={handleEditFormChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Zip Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="zipCode"
                          value={editFormData.zipCode}
                          onChange={handleEditFormChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <div className="d-flex justify-content-end">
                    <Button 
                      variant="secondary" 
                      className="me-2"
                      onClick={() => setIsEditing(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary">
                      Save Changes
                    </Button>
                  </div>
                </Form>
              ) : (
                // Display Client Info
                <div>
                  <p><strong>Email:</strong> {client.Email || 'N/A'}</p>
                  <p><strong>Phone:</strong> {client.Phone || 'N/A'}</p>
                  <p><strong>Address:</strong> {client.Address || 'N/A'}</p>
                  {client.City && client.State && (
                    <p>
                      <strong>City/State:</strong> {client.City}, {client.State} {client.ZipCode}
                    </p>
                  )}
                  <p><strong>Added:</strong> {new Date(client.CreatedAt).toLocaleDateString()}</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={6}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Projects</h5>
              <Button 
                as={Link} 
                to="/projects/create" 
                variant="primary" 
                size="sm"
              >
                New Project
              </Button>
            </Card.Header>
            <Card.Body>
              {clientProjects.length > 0 ? (
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>Project Name</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientProjects.map(project => (
                      <tr key={project.ProjectId}>
                        <td>{project.ProjectName}</td>
                        <td>{project.Status}</td>
                        <td>
                          <Button 
                            as={Link} 
                            to={`/projects/${project.ProjectId}`}
                            variant="primary"
                            size="sm"
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-center">No projects found for this client.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ClientDetail;
